import type { RequestStatus } from '@gestion-permisos/contracts';
import { ArrowRight, Ban, CheckCircle2, Clock3, FileText, type LucideIcon, Plus, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { COUNT_LABELS, REQUEST_STATUSES } from '../requests/format.js';
import { useMyRequestCount } from '../requests/requests-api.js';
import { useMe } from '../session/session.js';

const STATUS_ICONS: Record<RequestStatus, LucideIcon> = {
  DRAFT: FileText,
  PENDING_APPROVAL: Clock3,
  APPROVED: CheckCircle2,
  REJECTED: XCircle,
  CANCELLED: Ban,
};

/**
 * Inicio (W2), con el aspecto del de la Power App (`src/pages/home-page.tsx`): saludo, contadores de las
 * solicitudes propias por estado, accesos rápidos y las condiciones de `GET /me`. Cada contador es una
 * página de un elemento (`pageSize=1`) de la que solo se usa `total`: nunca se descarga la lista completa.
 * Los accesos y las condiciones son presentación; la API decide en cada pantalla.
 */
export function HomePage() {
  const me = useMe();
  const responsable = me.roles.includes('RESPONSABLE');
  const administrador = me.roles.includes('ADMINISTRADOR');
  const conditions = [
    me.roles.includes('EMPLEADO') && 'Empleado',
    responsable && 'Responsable',
    administrador && 'Administrador',
    me.isDirectorGeneral && 'Director General',
  ].filter((condition): condition is string => condition !== false);
  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <header className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div className="max-w-3xl space-y-1.5">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Hola, {me.fullName}</h1>
          <p className="text-sm leading-6 text-muted-foreground">Consulta el estado de tus permisos y accede a tus tareas principales.</p>
        </div>
        {me.canRequest ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Link to="/solicitudes/nueva" className={buttonClasses()}>
              <Plus aria-hidden className="size-4" />
              Nueva solicitud
            </Link>
          </div>
        ) : null}
      </header>
      <section aria-labelledby="contadores">
        <h2 id="contadores" className="sr-only">
          Mis solicitudes por estado
        </h2>
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {REQUEST_STATUSES.map((status) => (
            <StatusCounter key={status} status={status} />
          ))}
        </ul>
      </section>
      <div className="grid gap-5 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Accesos rápidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <QuickLink to="/mis-solicitudes">Ver mis solicitudes</QuickLink>
            {responsable || me.isDirectorGeneral ? <QuickLink to="/aprobaciones">Ir a aprobaciones</QuickLink> : null}
            {administrador ? <QuickLink to="/administracion">Abrir administración</QuickLink> : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Tu acceso</CardTitle>
          </CardHeader>
          <CardContent>
            {conditions.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {conditions.map((condition) => (
                  <li key={condition} className="rounded-md bg-secondary px-3 py-2 text-sm text-secondary-foreground">
                    {condition}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Sin roles asignados.</p>
            )}
            {me.isDirectorGeneral ? (
              <p className="mt-4 text-sm text-muted-foreground">
                Como Director General, puedes gestionar aprobaciones asignadas a ti, pero no crear solicitudes.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QuickLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className={buttonClasses('outline', 'w-full justify-between')}>
      {children}
      <ArrowRight aria-hidden className="size-4" />
    </Link>
  );
}

function StatusCounter({ status }: { status: RequestStatus }) {
  const count = useMyRequestCount(status);
  const Icon = STATUS_ICONS[status];
  if (count.isError) {
    return (
      <li className="sm:col-span-2 lg:col-span-3 xl:col-span-5">
        <ApiErrorView error={count.error} />
      </li>
    );
  }
  return (
    <li>
      <Link
        to={`/mis-solicitudes?estado=${status}`}
        aria-label={`${COUNT_LABELS[status]}: ${count.isPending ? 'cargando' : count.data}`}
        className="block h-full rounded-xl focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
      >
        <Card className="h-full transition-colors hover:border-ring">
          <CardContent className="flex items-center justify-between gap-3 py-1">
            <div className="min-w-0">
              <span className="block text-sm text-muted-foreground">{COUNT_LABELS[status]}</span>
              <span className="mt-2 block text-3xl font-semibold tracking-tight">{count.isPending ? '…' : count.data}</span>
            </div>
            <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Icon aria-hidden className="size-5" />
            </span>
          </CardContent>
        </Card>
      </Link>
    </li>
  );
}
